import React, { createContext, useContext, useEffect, useState, ReactNode, useRef } from 'react'
import { SocketManager } from '../SocketManager'
import { ConnectionStatus, SocketConfiguration } from '../types'

interface SocketContextType {
    socketManager: SocketManager
    status: ConnectionStatus
    isConnected: boolean
    getStatus: (namespace?: string) => ConnectionStatus
    isNamespaceConnected: (namespace?: string) => boolean
}

const SocketContext = createContext<SocketContextType | undefined>(undefined)

interface SocketProviderProps {
    children: ReactNode
    config: SocketConfiguration
    namespaces?: string[]
}

export const SocketProvider: React.FC<SocketProviderProps> = ({ children, config, namespaces = ['/'] }) => {
    const socketManager = SocketManager.getInstance()
    const [statuses, setStatuses] = useState<Record<string, ConnectionStatus>>({})
    const isFirstMount = useRef(true)

    useEffect(() => {
        socketManager.initialize(config)

        // Connect to each namespace
        namespaces.forEach(namespace => {
            socketManager.connect(namespace)
        })

        // Subscribe to status changes for each namespace
        const unsubscribes = namespaces.map(namespace => {
            return socketManager.onStatusChange(namespace, newStatus => {
                setStatuses(prev => ({ ...prev, [namespace]: newStatus }))
            })
        })

        // Initialize status state
        const initialStatuses: Record<string, ConnectionStatus> = {}
        namespaces.forEach(namespace => {
            initialStatuses[namespace] = socketManager.getStatus(namespace)
        })
        setStatuses(initialStatuses)

        // Cleanup: Only unsubscribe listeners, DON'T disconnect (singleton)
        return () => {
            unsubscribes.forEach(unsub => unsub())
        }
    }, [config.url, namespaces.join(',')]) // Reconnect when URL or namespaces change

    // Update auth options when token changes
    useEffect(() => {
        if (isFirstMount.current) {
            isFirstMount.current = false
            return
        }

        if (config.options?.auth?.token) {
            socketManager.updateOptions(config.options)
        }
    }, [config.options?.auth?.token])

    // Helper functions
    const getStatus = (namespace: string = '/'): ConnectionStatus => {
        return statuses[namespace] || 'disconnected'
    }

    const isNamespaceConnected = (namespace: string = '/'): boolean => {
        return getStatus(namespace) === 'connected'
    }

    const value: SocketContextType = {
        socketManager,
        status: getStatus('/'),
        isConnected: isNamespaceConnected('/'),
        getStatus,
        isNamespaceConnected,
    }
    console.log('isConnected', value.isConnected)

    return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>
}

export const useSocketContext = (): SocketContextType => {
    const context = useContext(SocketContext)
    if (!context) {
        throw new Error('useSocketContext must be used within a SocketProvider')
    }
    return context
}
